import React, { useEffect } from 'react';
import { Link } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useTheme, alpha } from '@mui/material/styles';
import { List as ListIcon } from 'lucide-react';
import type { AppDispatch } from 'src/redux/store';
import { useDataVault } from 'src/contexts/DataVaultContext';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import {
  fetchProfileSections,
  selectSections,
} from 'src/redux/slices/DataVault/dataVaultSectionsSlice';
import type { PQSection } from 'src/api/dataVault/profileSections';
import AISectionSummary from 'src/components/NewRenterInterface/DataVault/components/AISectionSummary';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';

function statusLabel(status: PQSection['status']): string {
  switch (status) {
    case 'complete':
      return 'Complete';
    case 'incomplete':
      return 'Incomplete';
    default:
      return 'Not started';
  }
}

export function PQIndexPage(): React.ReactElement {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const { surveyId, targetUserId, actingOnMemberId } = useDataVault();
  const sectionsEntry = useSelector(selectSections(targetUserId));
  const backPath = actingOnMemberId
    ? `/renter/data-vault/members/${actingOnMemberId}`
    : '/renter/data-vault';

  useEffect(() => {
    if (
      sectionsEntry?.status === 'loading' ||
      sectionsEntry?.status === 'succeeded' ||
      sectionsEntry?.status === 'failed'
    ) {
      return;
    }
    dispatch(fetchProfileSections({ targetUserId, actingOnMemberId }));
  }, [dispatch, targetUserId, actingOnMemberId, sectionsEntry?.status]);

  if (sectionsEntry?.status === 'loading') {
    return (
      <Box sx={{ bgcolor: 'background.default' }}>
        <PageHeader title="Pronto Questionnaire" backRoute={backPath} />
        <Box
          aria-busy="true"
          aria-label="Loading sections"
          sx={{ p: 2, pb: '100px' }}
        >
          <Stack spacing={1.5}>
            {[0, 1, 2].map((placeholder) => (
              <Box
                key={placeholder}
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
                  border: `1px solid ${theme.palette.divider}`,
                  py: '14px',
                  px: 2,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ gap: 1 }}
                >
                  <Stack spacing={0.75} sx={{ flexGrow: 1 }}>
                    <Skeleton
                      variant="text"
                      width="45%"
                      sx={{ fontSize: 14 }}
                      animation="wave"
                    />
                    <Skeleton
                      variant="text"
                      width="25%"
                      sx={{ fontSize: 12 }}
                      animation="wave"
                    />
                  </Stack>
                  <Skeleton
                    variant="rounded"
                    width={64}
                    height={30}
                    sx={{ borderRadius: '8px', flexShrink: 0 }}
                    animation="wave"
                  />
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    );
  }

  const sections = sectionsEntry?.sections ?? [];

  const hasIncompleteSection = sections.some((s) => s.status !== 'complete');

  const interviewPath = actingOnMemberId
    ? `/renter/data-vault/members/${actingOnMemberId}/pq/answer`
    : '/renter/data-vault/pq/answer';

  function sectionPath(sectionId: string): string {
    return actingOnMemberId
      ? `/renter/data-vault/members/${actingOnMemberId}/pq/sections/${sectionId}`
      : `/renter/data-vault/pq/sections/${sectionId}`;
  }

  function dotColor(status: PQSection['status']): string {
    switch (status) {
      case 'complete':
        return theme.palette.primary.main;
      case 'incomplete':
        return theme.palette.warning.dark;
      default:
        return theme.palette.text.disabled;
    }
  }

  function statusTextColor(status: PQSection['status']): string {
    switch (status) {
      case 'complete':
        return theme.palette.primary.main;
      case 'incomplete':
        return theme.palette.warning.dark;
      default:
        return theme.palette.text.disabled;
    }
  }

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      <PageHeader title="Pronto Questionnaire" backRoute={backPath} />

      <Box
        sx={{
          p: 2,
          pb: '100px',
        }}
      >
        {sectionsEntry?.status === 'failed' && (
          <Typography role="alert" variant="body2" color="error" sx={{ mb: 2 }}>
            Failed to load sections.{' '}
            <Box
              component="button"
              onClick={() =>
                dispatch(
                  fetchProfileSections({
                    targetUserId,
                    actingOnMemberId,
                    forceRefresh: true,
                  })
                )
              }
              sx={{
                background: 'none',
                border: 'none',
                color: 'error.main',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: 'inherit',
                p: 0,
              }}
            >
              Retry
            </Box>
          </Typography>
        )}

        <Stack spacing={1}>
          {sections.map((section) => {
            const isComplete = section.status === 'complete';
            const isIncomplete = section.status === 'incomplete';
            const remaining = section.totalCount - section.answeredCount;

            return (
              <Box
                key={section.id}
                component="article"
                aria-label={section.name}
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                {/* Header row */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ py: 1.5, px: 2 }}
                >
                  {/* Left group: dot + info */}
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '5px',
                        bgcolor: dotColor(section.status),
                        flexShrink: 0,
                      }}
                    />
                    <Stack spacing={0.25}>
                      <Typography
                        sx={{
                          color: 'text.primary',
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        {section.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: statusTextColor(section.status),
                          fontSize: 12,
                        }}
                      >
                        {isIncomplete
                          ? `Incomplete \u2014 Answer ${remaining} more question${
                              remaining === 1 ? '' : 's'
                            } to complete this section`
                          : statusLabel(section.status)}
                      </Typography>
                    </Stack>
                  </Stack>

                  {isComplete && (
                    <Box
                      component={Link}
                      to={sectionPath(section.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        borderRadius: '8px',
                        px: '10px',
                        py: '6px',
                        textDecoration: 'none',
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                      }}
                    >
                      <ListIcon size={14} color={theme.palette.primary.main} />
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: 'text.primary',
                        }}
                      >
                        See all
                      </Typography>
                    </Box>
                  )}
                </Stack>

                {/* AI summary for complete sections */}
                {isComplete && (
                  <AISectionSummary
                    surveyId={surveyId ?? 0}
                    sectionId={section.id}
                    compact
                  />
                )}
              </Box>
            );
          })}
        </Stack>

        {hasIncompleteSection && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              component={Link}
              to={interviewPath}
              onClick={() => dispatch(interviewActions.clearAllDeferred())}
              variant="contained"
              sx={{
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                fontSize: 15,
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Continue Questionnaire
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
